import React, { useMemo } from 'react';
import { Checkbox, FormControlLabel, Stack } from '@mui/material';
import {
  gridColumnFieldsSelector,
  gridColumnLookupSelector,
  GridColumnsPanel,
  gridColumnVisibilityModelSelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector
} from '@mui/x-data-grid-premium';
import { isEmpty } from 'lodash';

import {
  filterColumns,
  getAllGroupedColumns,
  sortGroupChildren,
  useColumnPanelActions
} from '../TableColumnServices/useColumnPanelActions';

import { ColumnGroup } from './ColumnGroup/ColumnGroup';

export const ColumnPanelBody = ({ search, hiddenColumns, ...rest }) => {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const columnLookup = useGridSelector(apiRef, gridColumnLookupSelector);
  const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
  const gridColumnsPosition = useGridSelector(apiRef, gridColumnFieldsSelector);

  const { toggleColumn } = useColumnPanelActions(apiRef);
  const columnGroupingModel = rootProps.columnGroupingModel;

  const allGroupedColumns = useMemo(() => {
    const groupedColumns = getAllGroupedColumns(columnGroupingModel, columnLookup).filter(column => {
      if ('field' in column) {
        if (hiddenColumns) {
          return !['actions', ...hiddenColumns].includes(column.field);
        }

        return column.field !== 'actions';
      }

      return true;
    });
    return sortGroupChildren(groupedColumns, gridColumnsPosition);
  }, [columnGroupingModel, columnLookup, gridColumnsPosition, hiddenColumns]);

  const searchResults = useMemo(() => {
    return filterColumns(allGroupedColumns, columnLookup, search);
  }, [allGroupedColumns, columnLookup, search]);

  if (!columnGroupingModel) {
    return <GridColumnsPanel {...rest} />;
  }

  return (
    <Stack direction="column" sx={{ maxHeight: '350px', overflowY: 'auto', p: '0 35px 12px' }}>
      {isEmpty(searchResults) && (
        <Stack
          sx={{
            width: '300px',
            textAlign: 'center'
          }}
        >
          No results were found. Try modifying your search criteria.
        </Stack>
      )}
      {allGroupedColumns.map(group => {
        if (group && 'field' in group) {
          if (columnLookup[group.field].headerName?.toUpperCase().includes(search.toUpperCase())) {
            return (
              <FormControlLabel
                key={group.field}
                control={<Checkbox checked={columnVisibilityModel[group.field] !== false} sx={{ p: 0.5 }} />}
                label={columnLookup[group.field].headerName}
                onChange={(_, newValue) => toggleColumn(group.field, newValue)}
              />
            );
          }

          return null;
        }

        return (
          <ColumnGroup
            search={search}
            group={group.headerClassName === 'empty-cell' ? group.children[0] : group}
            columnLookup={columnLookup}
            columnVisibilityModel={columnVisibilityModel}
            key={group.groupId}
            apiRef={apiRef}
          />
        );
      })}
    </Stack>
  );
};
